import { Button, Typography } from '@breetometer/components';
import { CurrencyBalance, InfoIcon } from 'BreetAssets';
import { Loader, Tooltip, TooltipContent, TransactionDetails } from 'BreetComponents';
import {
	type CURRENCYCODETYPE,
	getFiatWithdrawFee,
	type ITransactionDetailsRow,
	type IUseAccessCredentialsRequest,
} from 'BreetHelpers';
import {
	useCurrencyOutput,
	useFiatWithdrawRequestContext,
	useUserCurrencyProps,
	useVerifyTransaction,
	useWithdrawRequestContext,
} from 'BreetHooks';
import { useRequestPaymentMutation } from 'BreetRedux';
import { useCallback } from 'react';

const TransactionDetailsSection = () => {
	const { selectedWallet } = useWithdrawRequestContext();
	const { userWalletProps, withdrawRequestHookForm, meta } = useFiatWithdrawRequestContext();

	const [requestPayment] = useRequestPaymentMutation();

	const currencyOutput = useCurrencyOutput();
	const currencyProps = useUserCurrencyProps();

	const balance = userWalletProps.currencyAmount;

	const { watch: withdrawRequestWatch } = withdrawRequestHookForm;

	const amount = withdrawRequestWatch('amount');
	const narration = withdrawRequestWatch('narration');
	const selectedBeneficiary = withdrawRequestWatch('selectedBeneficiary');

	const amountNum = isNaN(Number(amount)) ? 0 : Number(amount);

	const walletCurrencyCode = selectedWallet?.currency ?? currencyProps?.code;

	const withdrawalFee = getFiatWithdrawFee(meta?.settings, walletCurrencyCode);

	const code = userWalletProps.code as unknown as CURRENCYCODETYPE;

	const totalDeducted = `${userWalletProps.symbol} ${currencyOutput(withdrawalFee + amountNum)}`;

	const isInsufficientFunds = withdrawalFee + amountNum > userWalletProps.walletBalance;

	const completeWithdrawRequest = useCallback(
		(amountArg: number, bankIdArg: string, narrationArg: string, data: IUseAccessCredentialsRequest) => {
			if (bankIdArg) {
				const defaultReq = {
					amount: amountArg,
					bankId: bankIdArg,
					pin: data.code ?? '',
				};
				requestPayment(narrationArg ? { ...defaultReq, narration: narrationArg } : defaultReq)
					.unwrap()
					.then(() => {
						data.onSuccess?.();
					})
					.catch((e: unknown) => {
						data.onError(e);
					});
			}
		},
		[requestPayment]
	);

	const handleWithdrawRequest = useCallback(
		(data?: IUseAccessCredentialsRequest) => {
			if (!selectedBeneficiary.id || !data?.code) return;

			completeWithdrawRequest(Number(amount), selectedBeneficiary.id, narration, data);
		},
		[amount, completeWithdrawRequest, selectedBeneficiary, narration]
	);

	const withdrawDetailsContent: ITransactionDetailsRow[] = [
		{
			label: 'Amount',
			value: `${userWalletProps.symbol} ${currencyOutput(amount)}`,
		},
		{
			label: 'Bank Account',
			bankDetails: {
				accountName: selectedBeneficiary.accountName,
				accountNumber: selectedBeneficiary.accountNumber,
				bankName: selectedBeneficiary.bankName,
			},
		},
	];

	const { handleVerification, userPreferenceLoading } = useVerifyTransaction({
		submitRequest: handleWithdrawRequest,
		closeModalId: 'requestPayment',
	});

	if (userPreferenceLoading) return <Loader />;

	return (
		<div className='transaction-details'>
			<TransactionDetails.Content>
				{withdrawDetailsContent.map((row) => (
					<TransactionDetails.Row key={row.label}>
						<TransactionDetails.RowLeft label={row.label} />

						<TransactionDetails.RowRight>
							{!!row.value && (
								<Typography className='transaction-details_content_row_value color-neutral-50'>{row.value}</Typography>
							)}
							{!!row.bankDetails && (
								<>
									<Typography className='transaction-details_content_row_accountname color-neutral-50'>
										{row.bankDetails.accountNumber}
									</Typography>
									<Typography
										as='span'
										className='transaction-details_content_row_accountdetails color-primary-50'
									>
										{row.bankDetails.bankName}
									</Typography>
									<Typography
										as='span'
										className='transaction-details_content_row_accountdetails color-primary-50'
									>
										{row.bankDetails.accountName}
									</Typography>
								</>
							)}
						</TransactionDetails.RowRight>
					</TransactionDetails.Row>
				))}

				{code === 'NGN' && (
					<TransactionDetails.Row>
						<div className='content-row-left content-row-left-narration'>
							<Typography className='transaction-details_content_row_label color-primary-50'>Withdrawal Fees</Typography>
							<Tooltip tipElement={<TooltipContent content='This fee covers stamp duty and charges paid to the bank.' />}>
								<InfoIcon className='info-icon svg-light' />
							</Tooltip>
						</div>
						<TransactionDetails.RowRight value={withdrawalFee} />
					</TransactionDetails.Row>
				)}

				{code === 'NGN' && (
					<TransactionDetails.Row
						label='Total Debit'
						value={totalDeducted}
					/>
				)}

				{narration && (
					<TransactionDetails.Row
						label='Narration'
						value={narration}
					/>
				)}
			</TransactionDetails.Content>

			<CurrencyBalance
				variant={code}
				balance={balance}
				className='svg-dark svg-dark-outline'
			/>

			{isInsufficientFunds && (
				<div className='transaction-details_insufficient-funds bg-dark-maroon'>
					<Typography className='color-error-100'>
						Insufficient Balance! Receive more crypto to complete your withdrawal.
					</Typography>
				</div>
			)}

			<Button
				label='Withdraw Funds'
				onClick={handleVerification}
				className='right-sheet_cta_fullwidth'
				type='button'
				disabled={isInsufficientFunds}
			/>
		</div>
	);
};

export default TransactionDetailsSection;
